import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";

import { handleAsync } from "@/utils";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import type {
  TChangeEmailRequest,
  TChangePhoneRequest,
  TDeliveryStatusResponse,
  TVerificationResponse,
  TVerificationStatusResponse,
  TVerifyOTPRequest,
} from "./verification.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class VerificationService {
  private rootEndpoint = "verification" as const;

  sendEmailVerification(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/email/send`, undefined, options)
    );
  }

  verifyEmail(payload: TVerifyOTPRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/email/confirm`, payload, options)
    );
  }

  resendEmailVerification(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/email/resend`, undefined, options)
    );
  }

  sendPhoneVerification(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/phone/send`, undefined, options)
    );
  }

  verifyPhone(payload: TVerifyOTPRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/phone/confirm`, payload, options)
    );
  }

  resendPhoneVerification(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/phone/resend`, undefined, options)
    );
  }

  getVerificationStatus(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationStatusResponse>(() =>
      apiService.get(`${this.rootEndpoint}/status`, options)
    );
  }

  /**
   * Request a verification OTP sent to a new email address.
   * Enumeration-safe: the server always returns the same public message;
   * `contact_updated` in the response indicates whether the change was applied.
   */
  changeEmail(payload: TChangeEmailRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/email/change`, payload, options)
    );
  }

  /**
   * Request a verification OTP sent to a new phone number (WhatsApp).
   * Enumeration-safe: use `contact_updated` to decide whether to update the store.
   */
  changePhone(payload: TChangePhoneRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TVerificationResponse>(() =>
      apiService.post(`${this.rootEndpoint}/phone/change`, payload, options)
    );
  }

  /**
   * Fetch the delivery status for a verification attempt using its correlation ID.
   * The `verification_request_id` is returned in every VerificationResponse.
   */
  getDeliveryStatus(verificationRequestId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TDeliveryStatusResponse>(() =>
      apiService.get(`${this.rootEndpoint}/${verificationRequestId}/status`, options)
    );
  }
}

export const verificationService = new VerificationService();
export type {
  TChangeEmailRequest,
  TChangePhoneRequest,
  TDeliveryStatusResponse,
  TVerificationResponse,
  TVerificationStatusResponse,
  TVerifyOTPRequest,
};
